import { Html, Line } from '@react-three/drei'
import { useMemo, useState } from 'react'

import { getShelfQuaternion } from '@components/storeMap/floor/3d/util'
import ShelfInfoPopoverContent from '@components/storeMap/floor/ShelfInfoPopoverContent'
import { useRegisterShelf } from '@components/storeMap/floor/ShelfRegistry'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'

import type { ThreeEvent } from '@react-three/fiber/dist/declarations/src/core/events'
import type { TransformedShelf } from '@state/shelf/types'

const FOOTPRINT_HEIGHT = 0.02

interface NoDataShelfProps {
  shelf: TransformedShelf
  color: string
}

/** A shelf on the map without an observation in the window: a dashed outline on the floor. */
function NoDataShelf({ shelf, color }: NoDataShelfProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { activeLayers } = useStoreMapContext()
  const shelfRef = useRegisterShelf(shelf.name)

  const { width, depth, rotation, center } = shelf
  const quaternion = useMemo(() => getShelfQuaternion(rotation), [rotation])

  const corners = useMemo((): [number, number, number][] => {
    const x = width / 2
    const z = depth / 2
    return [[-x, 0, -z], [x, 0, -z], [x, 0, z], [-x, 0, z], [-x, 0, -z]]
  }, [width, depth])

  function hoverHandler(e: ThreeEvent<PointerEvent>, hovered: boolean) {
    e.stopPropagation()
    setIsHovered(hovered)
  }

  return (
    <group position={[center.x, FOOTPRINT_HEIGHT, center.y]}>
      <group quaternion={quaternion}>
        <Line points={corners} color={color} lineWidth={1.5} dashed dashSize={0.2} gapSize={0.12} />

        {/* the line is too thin to hit, this invisible plate takes the hover */}
        <mesh
          ref={shelfRef}
          onPointerOver={e => hoverHandler(e, true)}
          onPointerOut={e => hoverHandler(e, false)}
        >
          <boxGeometry args={[width, FOOTPRINT_HEIGHT, depth]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      </group>

      {isHovered && (
        <Html style={{ pointerEvents: 'none' }}>
          <div className='rounded-sm bg-bg-primary-solid p-2 whitespace-nowrap text-white shadow-lg'>
            <ShelfInfoPopoverContent shelf={shelf} activeLayers={activeLayers} />
          </div>
        </Html>
      )}
    </group>
  )
}

export default NoDataShelf
